import React from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import { PRES, SPACE, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { PresentationSettingsState } from '../presentationSettings';
import { AdminLabeledControl } from './AdminInfoLabel';
import AdminSettingCard from './AdminSettingCard';
import { adminFieldSx } from './adminFormSx';

interface AdminFieldsSectionProps {
  draft: PresentationSettingsState;
  setDraft: React.Dispatch<React.SetStateAction<PresentationSettingsState>>;
}

const AdminFieldsSection: React.FC<AdminFieldsSectionProps> = ({ draft, setDraft }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${SPACE[5]}px`, maxWidth: 720 }}>
    <Typography sx={{ color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.body }}>
      {COPY.settingsFieldAliasesHelp}
    </Typography>
    <Typography sx={{ color: PRES.textTertiary, fontFamily: TYPE.ui, ...TYPE.caption }}>
      {COPY.settingsFieldAliasExample}
    </Typography>
    {draft.logicalFields.map((field, index) => (
      <AdminSettingCard key={field.id} title={field.label || field.id}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: 2, mb: 2 }}>
          <AdminLabeledControl label={COPY.settingsFieldId}>
            {({ id }) => (
              <TextField
                id={id}
                size="small"
                fullWidth
                value={field.id}
                disabled
                sx={adminFieldSx()}
              />
            )}
          </AdminLabeledControl>
          <AdminLabeledControl label={COPY.settingsFieldLabel}>
            {({ id }) => (
              <TextField
                id={id}
                size="small"
                fullWidth
                value={field.label}
                onChange={(event) => {
                  const logicalFields = draft.logicalFields.map((item, i) => (
                    i === index ? { ...item, label: event.target.value } : item
                  ));
                  setDraft((current) => ({ ...current, logicalFields }));
                }}
                sx={adminFieldSx()}
              />
            )}
          </AdminLabeledControl>
        </Box>
        <AdminLabeledControl label={COPY.settingsAliases} hint={COPY.settingsFieldAliasHint}>
          {({ id, describedBy }) => (
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={field.aliases}
              onChange={(_, value) => {
                const aliases = value.map((item) => String(item).trim()).filter(Boolean);
                if (!aliases.length) return;
                const logicalFields = draft.logicalFields.map((item, i) => (
                  i === index ? { ...item, aliases } : item
                ));
                setDraft((current) => ({ ...current, logicalFields }));
              }}
              renderTags={(value, getTagProps) => value.map((option, tagIndex) => {
                const tagProps = getTagProps({ index: tagIndex });
                const last = value.length <= 1;
                return (
                  <Chip
                    {...tagProps}
                    key={`${option}-${tagIndex}`}
                    label={option}
                    disabled={last}
                    onDelete={last ? undefined : tagProps.onDelete}
                  />
                );
              })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id={id}
                  size="small"
                  inputProps={{ ...params.inputProps, 'aria-describedby': describedBy }}
                  sx={adminFieldSx()}
                />
              )}
            />
          )}
        </AdminLabeledControl>
        {field.aliases.length <= 1 && (
          <Typography sx={{ mt: 1, color: PRES.textTertiary, fontFamily: TYPE.ui, ...TYPE.caption }}>
            {COPY.settingsLastAlias}
          </Typography>
        )}
      </AdminSettingCard>
    ))}
  </Box>
);

export default AdminFieldsSection;
